import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Card, Form, Input, Button, Select, InputNumber,
  notification, Row, Col, Typography, Result,
  Modal, Slider, DatePicker, Steps,
} from 'antd';
import {
  PhoneOutlined, UserOutlined, WhatsAppOutlined,
  ShopOutlined, EnvironmentOutlined, MailOutlined,
  GlobalOutlined, CameraOutlined, PlusOutlined,
  LoadingOutlined, ScissorOutlined, CheckCircleOutlined,
  EditOutlined,
} from '@ant-design/icons';
import Cropper from 'react-easy-crop';
import moment from 'moment';
import axios from 'axios';
import { baseUrl } from '../config';

// ── Canvas crop helper ─────────────────────────────────────────────────────────
const createImage = (url) => new Promise((resolve, reject) => {
  const img = new Image();
  img.addEventListener('load', () => resolve(img));
  img.addEventListener('error', reject);
  img.setAttribute('crossOrigin', 'anonymous');
  img.src = url;
});

async function getCroppedBlob(imageSrc, pixelCrop) {
  const image  = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx    = canvas.getContext('2d');
  const maxDim = 900;
  const scale  = Math.min(maxDim / pixelCrop.width, maxDim / pixelCrop.height, 1);
  canvas.width  = Math.round(pixelCrop.width  * scale);
  canvas.height = Math.round(pixelCrop.height * scale);
  ctx.drawImage(image, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, 0, 0, canvas.width, canvas.height);
  return new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.85));
}

async function doUploadBlob(blob, type) {
  const fd = new FormData();
  fd.append('photo', blob, 'photo.jpg');
  const res  = await fetch(`${baseUrl}/business-directory/upload-photo?type=${type}`, { method: 'POST', body: fd });
  const data = await res.json();
  if (!data.status) throw new Error(data.message || 'Upload failed');
  return data.url;
}

// ── CropModal ──────────────────────────────────────────────────────────────────
function CropModal({ open, imageSrc, aspect, onDone, onCancel }) {
  const [crop, setCrop]       = useState({ x: 0, y: 0 });
  const [zoom, setZoom]       = useState(1);
  const [croppedPixels, setCroppedPixels] = useState(null);
  const [applying, setApplying] = useState(false);
  const onCropComplete = useCallback((_, pixels) => setCroppedPixels(pixels), []);

  const apply = async () => {
    if (!croppedPixels) return;
    setApplying(true);
    try { onDone(await getCroppedBlob(imageSrc, croppedPixels)); }
    catch { notification.error({ message: 'Crop विफल हुआ।', placement: 'top' }); }
    finally { setApplying(false); }
  };

  return (
    <Modal open={open} onCancel={onCancel} width={360} centered destroyOnClose
      title={<span><ScissorOutlined style={{ marginRight: 8, color: '#7a0000' }} />फोटो काटें और सेट करें</span>}
      footer={[
        <Button key="c" onClick={onCancel}>रद्द करें</Button>,
        <Button key="a" type="primary" loading={applying} onClick={apply}
          style={{ background: '#7a0000', borderColor: '#7a0000' }}>✓ सेट करें</Button>,
      ]}>
      <div style={{ position: 'relative', height: 260, background: '#111', borderRadius: 8, overflow: 'hidden' }}>
        {imageSrc && (
          <Cropper image={imageSrc} crop={crop} zoom={zoom} aspect={aspect}
            onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={onCropComplete} />
        )}
      </div>
      <div style={{ marginTop: 12 }}>
        <div style={{ fontSize: 12, color: '#888', marginBottom: 6 }}>ज़ूम करें</div>
        <Slider min={1} max={3} step={0.05} value={zoom} onChange={setZoom}
          trackStyle={{ background: '#7a0000' }} handleStyle={{ borderColor: '#7a0000' }} />
      </div>
    </Modal>
  );
}

// ── Profile photo upload ───────────────────────────────────────────────────────
const ProfilePhotoUpload = ({ value, onChange }) => {
  const [preview, setPreview]     = useState(value || null);
  const [uploading, setUploading] = useState(false);
  const [rawSrc, setRawSrc]       = useState(null);
  const [cropOpen, setCropOpen]   = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { setPreview(value || null); }, [value]);

  const handleFile = (file) => {
    if (!file) return;
    if (!/^image\/(jpeg|jpg|png|webp)$/i.test(file.type)) {
      notification.warning({ message: 'JPG या PNG चुनें।', placement: 'top' }); return;
    }
    const reader = new FileReader();
    reader.onload = () => { setRawSrc(reader.result); setCropOpen(true); };
    reader.readAsDataURL(file);
    if (inputRef.current) inputRef.current.value = '';
  };

  const onCropDone = async (blob) => {
    setCropOpen(false);
    setUploading(true);
    try {
      const url = await doUploadBlob(blob, 'profile');
      setPreview(url); onChange(url);
    } catch (err) {
      notification.error({ message: 'अपलोड विफल: ' + err.message, placement: 'top' });
    } finally { setUploading(false); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
      <input ref={inputRef} type="file" accept="image/*" style={{ display: 'none' }}
        onChange={e => handleFile(e.target.files[0])} />
      <div style={{ position: 'relative', width: 90, height: 90 }}>
        <div onClick={() => !uploading && inputRef.current?.click()}
          style={{ width: 90, height: 90, borderRadius: 10, border: '3px solid #7a0000',
            overflow: 'hidden', cursor: 'pointer', background: '#f0f2f5' }}>
          {preview
            ? <img src={preview} alt="profile"
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            : <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                {uploading
                  ? <LoadingOutlined style={{ fontSize: 28, color: '#7a0000' }} />
                  : <UserOutlined style={{ fontSize: 36, color: '#ccc' }} />
                }
              </div>
          }
        </div>
        <div onClick={() => !uploading && inputRef.current?.click()}
          style={{ position: 'absolute', bottom: -4, right: -4, width: 26, height: 26,
            borderRadius: 8, background: '#7a0000', border: '2px solid #fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
          <CameraOutlined style={{ color: '#fff', fontSize: 12 }} />
        </div>
        {preview && !uploading && (
          <button type="button"
            onClick={e => { e.stopPropagation(); setPreview(null); onChange(null); }}
            style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20,
              borderRadius: '50%', background: '#ff4d4f', border: '2px solid #fff',
              color: '#fff', fontSize: 10, cursor: 'pointer', lineHeight: '16px',
              textAlign: 'center', padding: 0 }}>✕</button>
        )}
      </div>
      <span style={{ fontSize: 10, color: '#aaa', marginTop: 6 }}>
        {preview ? 'बदलने के लिए क्लिक करें' : 'फोटो · max 5MB'}
      </span>
      <CropModal open={cropOpen} imageSrc={rawSrc} aspect={1}
        onDone={onCropDone} onCancel={() => setCropOpen(false)} />
    </div>
  );
};

// ── Business multi-photo upload ────────────────────────────────────────────────
const MultiPhotoUpload = ({ value, onChange }) => {
  const [slots, setSlots]     = useState(() => { const u = value ? value.split(',').filter(Boolean) : []; return [u[0]||null, u[1]||null]; });
  const [uploading, setUploading] = useState([false, false]);
  const [rawSrc, setRawSrc]   = useState(null);
  const [cropOpen, setCropOpen] = useState(false);
  const [pendingIdx, setPendingIdx] = useState(null);
  const refs = [useRef(null), useRef(null)];

  useEffect(() => {
    const u = value ? value.split(',').filter(Boolean) : [];
    setSlots([u[0]||null, u[1]||null]);
  }, [value]);

  const notify = (s) => onChange(s.filter(Boolean).join(',') || null);

  const handleFile = (file, idx) => {
    if (!file) return;
    if (!/^image\/(jpeg|jpg|png|webp)$/i.test(file.type)) {
      notification.warning({ message: 'JPG या PNG चुनें।', placement: 'top' }); return;
    }
    const reader = new FileReader();
    reader.onload = () => { setRawSrc(reader.result); setPendingIdx(idx); setCropOpen(true); };
    reader.readAsDataURL(file);
    if (refs[idx].current) refs[idx].current.value = '';
  };

  const onCropDone = async (blob) => {
    setCropOpen(false);
    const idx = pendingIdx;
    setUploading(u => { const n=[...u]; n[idx]=true; return n; });
    try {
      const url = await doUploadBlob(blob, 'business');
      setSlots(s => { const n=[...s]; n[idx]=url; notify(n); return n; });
    } catch (err) {
      notification.error({ message: 'अपलोड विफल: ' + err.message, placement: 'top' });
    } finally { setUploading(u => { const n=[...u]; n[idx]=false; return n; }); }
  };

  const remove = (idx) => {
    setSlots(s => { const n=[...s]; n[idx]=null; notify(n); return n; });
    if (refs[idx].current) refs[idx].current.value = '';
  };

  return (
    <>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap',
        border: '2px dashed #7a0000', borderRadius: 10, padding: '10px 12px',
        background: '#f8faff', minHeight: 96 }}>
        {[0,1].map(idx => (
          <input key={idx} ref={refs[idx]} type="file" accept="image/*" style={{ display: 'none' }}
            onChange={e => handleFile(e.target.files[0], idx)} />
        ))}
        {[0,1].map(idx => (
          slots[idx] ? (
            <div key={idx} style={{ position: 'relative', flexShrink: 0 }}>
              <img src={slots[idx]} alt={`biz-${idx+1}`} onClick={() => refs[idx].current?.click()}
                style={{ width: 80, height: 80, borderRadius: 8, objectFit: 'cover',
                  border: '2px solid #7a0000', cursor: 'pointer', display: 'block' }} />
              <button type="button" onClick={() => remove(idx)}
                style={{ position: 'absolute', top: -6, right: -6, width: 18, height: 18,
                  borderRadius: '50%', background: '#ff4d4f', border: '2px solid #fff',
                  color: '#fff', fontSize: 9, cursor: 'pointer', lineHeight: '14px',
                  textAlign: 'center', padding: 0 }}>✕</button>
            </div>
          ) : uploading[idx] ? (
            <div key={idx} style={{ width: 80, height: 80, borderRadius: 8,
              border: '2px dashed #7a0000', background: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <LoadingOutlined style={{ fontSize: 20, color: '#7a0000' }} />
            </div>
          ) : (
            <button key={idx} type="button" onClick={() => refs[idx].current?.click()}
              style={{ width: 80, height: 80, borderRadius: 8, border: '2px dashed #7a0000',
                background: '#fff', cursor: 'pointer', display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', gap: 4, flexShrink: 0 }}>
              <PlusOutlined style={{ fontSize: 20, color: '#7a0000' }} />
              <span style={{ fontSize: 10, color: '#7a0000' }}>फोटो {idx+1}</span>
            </button>
          )
        ))}
        <div style={{ flex: 1, color: '#bbb', fontSize: 11, lineHeight: 1.8, minWidth: 80 }}>
          <CameraOutlined style={{ fontSize: 16, color: '#7a0000', display: 'block', marginBottom: 4 }} />
          दुकान की फोटो<br />JPG / PNG<br />max 5MB
        </div>
      </div>
      <CropModal open={cropOpen} imageSrc={rawSrc} aspect={4/3}
        onDone={onCropDone} onCancel={() => setCropOpen(false)} />
    </>
  );
};

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { Item } = Form;
const { Step } = Steps;

// ── Validation ─────────────────────────────────────────────────────────────────
const URL_PATTERN  = /(https?:\/\/|www\.|\.com|\.net|\.org|\.in|\.co|<[^>]+>|javascript:|on\w+=|&lt;|&#|<script|SELECT |INSERT |DROP |DELETE |UPDATE )/i;
const NAME_PATTERN = /^[a-zA-Zऀ-ॿ\s.''\-]+$/;

const validateSafe = (label) => (_, value) => {
  if (!value || !value.trim()) return Promise.resolve();
  if (URL_PATTERN.test(value)) return Promise.reject(`${label} में link, script या HTML की अनुमति नहीं है।`);
  return Promise.resolve();
};

const validateName = (label) => (_, value) => {
  if (!value || !value.trim()) return Promise.reject(`${label} दर्ज करें।`);
  if (URL_PATTERN.test(value)) return Promise.reject('Link या script की अनुमति नहीं है।');
  if (!NAME_PATTERN.test(value.trim())) return Promise.reject(`${label} में केवल अक्षर लिखें।`);
  return Promise.resolve();
};

const phoneRules = [
  { required: true, message: 'मोबाइल नंबर आवश्यक है।' },
  {
    validator: (_, value) => {
      if (!value) return Promise.reject('मोबाइल नंबर दर्ज करें।');
      if (!/^\d+$/.test(value)) return Promise.reject('केवल अंक दर्ज करें।');
      if (value.length !== 10) return Promise.reject('10 अंकों का नंबर होना चाहिए।');
      if (!/^[6-9]/.test(value)) return Promise.reject('6, 7, 8 या 9 से शुरू होना चाहिए।');
      return Promise.resolve();
    },
  },
];

const OTP_TIMEOUT = 120;

// ── Main component ─────────────────────────────────────────────────────────────
function BusinessDirectoryUpdate() {
  const [step, setStep]             = useState(1);
  const [phone, setPhone]           = useState('');
  const [otp, setOtp]               = useState('');
  const [sessionToken, setSessionToken] = useState('');
  const [sectors, setSectors]       = useState([]);
  const [selectedSector, setSelectedSector] = useState(null);
  const [timer, setTimer]           = useState(0);
  const [sending, setSending]       = useState(false);
  const [verifying, setVerifying]   = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();
  const timerRef = useRef(null);

  // Pre-fill phone from ?phone= query param
  useEffect(() => {
    const p = new URLSearchParams(window.location.search).get('phone');
    if (p && /^[6-9]\d{9}$/.test(p)) setPhone(p);
    return () => clearInterval(timerRef.current);
  }, []);

  const startTimer = () => {
    setTimer(OTP_TIMEOUT);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setTimer(t => { if (t <= 1) { clearInterval(timerRef.current); return 0; } return t - 1; });
    }, 1000);
  };

  const sendOtp = async () => {
    if (!/^\d{10}$/.test(phone) || !/^[6-9]/.test(phone)) {
      notification.warning({ message: 'सही 10-अंकों का मोबाइल नंबर दर्ज करें।', placement: 'top' });
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(`${baseUrl}/business-directory/send-otp`, { phone });
      if (res.data.status) {
        setStep(2);
        startTimer();
        notification.success({
          message: 'OTP भेजा गया!',
          description: `WhatsApp पर ${phone} को OTP भेजा गया है।`,
          placement: 'top',
        });
      } else {
        notification.error({ message: res.data.message || 'OTP भेजने में विफल।', placement: 'top' });
      }
    } catch (err) {
      notification.error({ message: err?.response?.data?.message || 'OTP भेजने में विफल।', placement: 'top' });
    } finally { setSending(false); }
  };

  const verifyOtp = async () => {
    if (otp.length !== 6) {
      notification.warning({ message: '6-अंकों का OTP दर्ज करें।', placement: 'top' });
      return;
    }
    setVerifying(true);
    try {
      const res = await axios.post(`${baseUrl}/business-directory/verify-otp`, { phone, otp });
      if (res.data.status) {
        const token = res.data.token;
        setSessionToken(token);
        // Fetch entry + sectors in parallel — only after OTP is verified
        const [entryRes, sectorsRes] = await Promise.all([
          axios.get(`${baseUrl}/business-directory/my-entry`, { params: { token } }),
          fetch(`${baseUrl}/business-sector/public-list`).then(r => r.json()).catch(() => ({ status: false })),
        ]);
        if (sectorsRes.status) setSectors(sectorsRes.result || []);
        if (entryRes.data.status) {
          const e = entryRes.data.result;
          setSelectedSector(e.sector_id ? String(e.sector_id) : null);
          form.setFieldsValue({
            name:               e.name,
            father_name:        e.father_name,
            phone_no:           e.phone_no,
            sector:             e.sector_id ? String(e.sector_id) : undefined,
            sub_sector:         e.sub_sector_id ? String(e.sub_sector_id) : undefined,
            firm_name:          e.firm_name,
            establishment_year: e.establishment_year,
            business_about:     e.business_about,
            business_address:   e.business_address,
            village:            e.village,
            tehsil:             e.tehsil,
            district:           e.district,
            dob:                e.dob && e.dob !== '0000-00-00' ? moment(e.dob) : null,
            gotra:              e.gotra,
            email:              e.email,
            alternate_phone:    e.alternate_phone,
            current_city:       e.current_city,
            state:              e.state,
            profile_photo:      e.profile_photo,
            business_photo:     e.business_photo,
            experience_years:   e.experience_years,
            biz_rank:           e.biz_rank,
            post:               e.post,
          });
          setStep(3);
        } else {
          notification.error({ message: entryRes.data.message || 'आपकी entry नहीं मिली।', placement: 'top' });
        }
      } else {
        notification.error({ message: res.data.message || 'OTP गलत है।', placement: 'top' });
      }
    } catch (err) {
      notification.error({ message: err?.response?.data?.message || 'OTP सत्यापन विफल।', placement: 'top' });
    } finally { setVerifying(false); }
  };

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      const sectorObj    = sectors.find(s => String(s.id) === String(values.sector));
      const sectorLabel  = sectorObj?.name || '';
      const subLabel     = (sectorObj?.sub_sectors || []).find(s => String(s.id) === String(values.sub_sector))?.name || '';

      const payload = {
        token:              sessionToken,
        name:               values.name,
        father_name:        values.father_name || null,
        phone_no:           values.phone_no,
        sector_id:          values.sector || null,
        sector_label:       sectorLabel,
        sub_sector_id:      values.sub_sector || null,
        sub_sector_label:   subLabel,
        firm_name:          values.firm_name || null,
        establishment_year: values.establishment_year || null,
        business_about:     values.business_about || null,
        business_address:   values.business_address || null,
        village:            values.village || null,
        tehsil:             values.tehsil || null,
        district:           values.district || null,
        dob:                values.dob ? moment(values.dob).format('YYYY-MM-DD') : null,
        gotra:              values.gotra || null,
        email:              values.email || null,
        alternate_phone:    values.alternate_phone || null,
        current_city:       values.current_city || null,
        state:              values.state || null,
        profile_photo:      values.profile_photo || null,
        business_photo:     values.business_photo || null,
        experience_years:   values.experience_years || null,
        biz_rank:           values.biz_rank || null,
        post:               values.post || null,
      };

      const res = await axios.post(`${baseUrl}/business-directory/request-update`, payload);
      if (res.data.status) {
        setStep(4);
      } else {
        notification.error({ message: res.data.message || 'अपडेट अनुरोध विफल।', placement: 'top' });
      }
    } catch (err) {
      if (err?.errorFields) return;
      notification.error({ message: err?.response?.data?.message || 'सबमिट विफल।', placement: 'top' });
    } finally { setSubmitting(false); }
  };

  const resetAll = () => {
    setStep(1); setPhone(''); setOtp('');
    setSessionToken(''); setSelectedSector(null);
    form.resetFields();
    clearInterval(timerRef.current);
  };

  const subOptions = selectedSector
    ? (sectors.find(s => String(s.id) === String(selectedSector))?.sub_sectors || [])
    : [];

  const cardStyle = {
    maxWidth: 680, margin: '0 auto',
    borderRadius: 16,
    boxShadow: '0 4px 24px rgba(26,46,74,0.10)',
    border: 'none',
  };

  const sectionStyle = {
    fontSize: 11, fontWeight: 700, color: '#8c8c8c',
    textTransform: 'uppercase', letterSpacing: '1.2px',
    marginBottom: 12, marginTop: 8,
    paddingBottom: 6, borderBottom: '1px solid #f0f0f0',
  };

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%)',
      padding: '32px 16px 64px' }}>

      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 28 }}>
        <div style={{ width: 64, height: 64, borderRadius: '50%',
          background: 'linear-gradient(135deg, #7a0000, #2a4a6b)',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
          <EditOutlined style={{ fontSize: 28, color: '#d4a84b' }} />
        </div>
        <Title level={3} style={{ color: '#7a0000', marginBottom: 4, fontWeight: 700 }}>
          व्यवसाय जानकारी अपडेट करें
        </Title>
        <Text type="secondary" style={{ fontSize: 13 }}>
          ABRSVS व्यवसाय निर्देशिका में अपनी जानकारी अपडेट करें
        </Text>
      </div>

      {/* Steps indicator */}
      {step < 4 && (
        <div style={{ maxWidth: 400, margin: '0 auto 28px' }}>
          <Steps current={step - 1} size="small">
            <Step title="मोबाइल" />
            <Step title="OTP सत्यापन" />
            <Step title="फॉर्म अपडेट" />
          </Steps>
        </div>
      )}

      {/* Step 1 — phone number */}
      {step === 1 && (
        <Card style={cardStyle}>
          <div style={{ textAlign: 'center', marginBottom: 28 }}>
            <WhatsAppOutlined style={{ fontSize: 44, color: '#25D366', marginBottom: 10 }} />
            <Title level={4} style={{ color: '#7a0000', marginBottom: 6 }}>WhatsApp OTP सत्यापन</Title>
            <Text type="secondary" style={{ fontSize: 13 }}>
              जब आपने फॉर्म भरा था, वही मोबाइल नंबर दर्ज करें।<br />
              आपको WhatsApp पर OTP मिलेगा।
            </Text>
          </div>
          <div style={{ maxWidth: 320, margin: '0 auto' }}>
            <Input
              size="large"
              prefix={<PhoneOutlined style={{ color: '#25D366' }} />}
              placeholder="10-अंकों का मोबाइल नंबर"
              value={phone}
              maxLength={10}
              onChange={e => setPhone(e.target.value.replace(/\D/g, ''))}
              onKeyDown={e => { if (e.key === 'Enter') sendOtp(); }}
              style={{ borderRadius: 10, marginBottom: 16, fontSize: 16 }}
            />
            <Button block size="large" loading={sending} onClick={sendOtp}
              icon={<WhatsAppOutlined />}
              style={{ background: '#25D366', borderColor: '#25D366', color: '#fff',
                borderRadius: 10, fontWeight: 700, height: 48, fontSize: 15 }}>
              WhatsApp OTP भेजें
            </Button>
          </div>
        </Card>
      )}

      {/* Step 2 — OTP entry */}
      {step === 2 && (
        <Card style={cardStyle}>
          <div style={{ textAlign: 'center', marginBottom: 28 }}>
            <CheckCircleOutlined style={{ fontSize: 44, color: '#7a0000', marginBottom: 10 }} />
            <Title level={4} style={{ color: '#7a0000', marginBottom: 6 }}>OTP दर्ज करें</Title>
            <Text type="secondary" style={{ fontSize: 13 }}>
              <strong>{phone}</strong> पर WhatsApp से 6-अंकों का OTP भेजा गया है।
            </Text>
          </div>
          <div style={{ maxWidth: 320, margin: '0 auto' }}>
            <Input
              size="large"
              placeholder="• • • • • •"
              value={otp}
              maxLength={6}
              onChange={e => setOtp(e.target.value.replace(/\D/g, ''))}
              onKeyDown={e => { if (e.key === 'Enter') verifyOtp(); }}
              style={{ borderRadius: 10, marginBottom: 16, fontSize: 24,
                letterSpacing: 10, textAlign: 'center', fontWeight: 700 }}
            />
            <Button block size="large" loading={verifying} onClick={verifyOtp}
              style={{ background: '#7a0000', borderColor: '#7a0000', color: '#fff',
                borderRadius: 10, fontWeight: 700, height: 48, fontSize: 15, marginBottom: 16 }}>
              OTP सत्यापित करें
            </Button>
            <div style={{ textAlign: 'center' }}>
              {timer > 0
                ? <Text type="secondary" style={{ fontSize: 13 }}>
                    दोबारा भेजें {Math.floor(timer / 60)}:{String(timer % 60).padStart(2, '0')} में
                  </Text>
                : <Button type="link" onClick={() => { setOtp(''); sendOtp(); }}
                    style={{ color: '#25D366', fontWeight: 600, padding: 0 }}>
                    OTP दोबारा भेजें
                  </Button>
              }
            </div>
            <div style={{ textAlign: 'center', marginTop: 8 }}>
              <Button type="link" onClick={() => { setStep(1); setOtp(''); }}
                style={{ color: '#999', fontSize: 12 }}>
                ← नंबर बदलें
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Step 3 — edit form */}
      {step === 3 && (
        <Card style={{ ...cardStyle, maxWidth: 720 }}>
          <div style={{ background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 8,
            padding: '10px 14px', marginBottom: 20, fontSize: 13, color: '#614700' }}>
            आपकी मौजूदा जानकारी नीचे दिखाई गई है — जो भी बदलना हो बदलें और सबमिट करें।
            <strong> बदलाव admin की मंजूरी के बाद ही लागू होंगे। तब तक पुरानी जानकारी directory में दिखती रहेगी।</strong>
          </div>

          <Form form={form} layout="vertical">
            {/* Personal */}
            <div style={sectionStyle}>व्यक्तिगत जानकारी</div>
            <Row gutter={[16, 0]}>
              <Col xs={24} sm={12}>
                <Item label="पूरा नाम" name="name"
                  rules={[{ required: true }, { validator: validateName('पूरा नाम') }]}>
                  <Input prefix={<UserOutlined />} placeholder="पूरा नाम" maxLength={100} />
                </Item>
              </Col>
              <Col xs={24} sm={12}>
                <Item label="पिता का नाम" name="father_name"
                  rules={[{ validator: validateName('पिता का नाम') }]}>
                  <Input prefix={<UserOutlined />} placeholder="पिता का नाम" maxLength={100} />
                </Item>
              </Col>
              <Col xs={24} sm={16}>
                <Item label="WhatsApp / मोबाइल" name="phone_no" rules={phoneRules}>
                  <Input prefix={<PhoneOutlined />} placeholder="10-digit number" maxLength={10}
                    onKeyDown={e => { if (!/[0-9]/.test(e.key) && !['Backspace','Delete','Tab','ArrowLeft','ArrowRight'].includes(e.key)) e.preventDefault(); }} />
                </Item>
              </Col>
              <Col xs={24} sm={8}>
                <Item label="प्रोफाइल फोटो" name="profile_photo">
                  <ProfilePhotoUpload />
                </Item>
              </Col>
              <Col xs={24} sm={12}>
                <Item label="वैकल्पिक नंबर" name="alternate_phone">
                  <Input prefix={<PhoneOutlined />} placeholder="Alternate number" maxLength={10}
                    onKeyDown={e => { if (!/[0-9]/.test(e.key) && !['Backspace','Delete','Tab','ArrowLeft','ArrowRight'].includes(e.key)) e.preventDefault(); }} />
                </Item>
              </Col>
              <Col xs={24} sm={12}>
                <Item label="जन्म तिथि" name="dob">
                  <DatePicker style={{ width: '100%' }} format="DD-MM-YYYY" placeholder="DD-MM-YYYY"
                    disabledDate={d => d && d.isAfter(moment())} />
                </Item>
              </Col>
              <Col xs={24} sm={12}>
                <Item label="गोत्र" name="gotra">
                  <Input prefix={<UserOutlined />} placeholder="गोत्र" maxLength={100} />
                </Item>
              </Col>
              <Col xs={24} sm={12}>
                <Item label="ईमेल" name="email"
                  rules={[{ type: 'email', message: 'Valid email required' }]}>
                  <Input prefix={<MailOutlined />} placeholder="email@example.com" maxLength={255} />
                </Item>
              </Col>
            </Row>

            {/* Business */}
            <div style={sectionStyle}>व्यवसाय की जानकारी</div>
            <Row gutter={[16, 0]}>
              <Col xs={24} sm={12}>
                <Item label="मुख्य क्षेत्र" name="sector">
                  <Select placeholder="Select sector" showSearch optionFilterProp="children"
                    onChange={val => { setSelectedSector(val); form.setFieldsValue({ sub_sector: undefined }); }}
                    virtual={false} listHeight={220} getPopupContainer={trigger => trigger.parentNode}>
                    {sectors.map(s => <Option key={s.id} value={String(s.id)}>{s.name}</Option>)}
                  </Select>
                </Item>
              </Col>
              <Col xs={24} sm={12}>
                <Item label="उप-क्षेत्र" name="sub_sector">
                  <Select placeholder={selectedSector ? 'Select sub-sector' : 'पहले मुख्य क्षेत्र चुनें'}
                    disabled={!selectedSector} showSearch optionFilterProp="children"
                    virtual={false} listHeight={220} getPopupContainer={trigger => trigger.parentNode}>
                    {subOptions.map(s => <Option key={s.id} value={String(s.id)}>{s.name}</Option>)}
                  </Select>
                </Item>
              </Col>
              <Col xs={24} sm={12}>
                <Item label="फर्म / दुकान का नाम" name="firm_name"
                  rules={[{ validator: validateSafe('फर्म/दुकान का नाम') }]}>
                  <Input prefix={<ShopOutlined />} placeholder="फर्म / दुकान का नाम" maxLength={200} />
                </Item>
              </Col>
              <Col xs={24} sm={12}>
                <Item label="स्थापना वर्ष" name="establishment_year"
                  rules={[{ type: 'number', min: 1900, max: new Date().getFullYear() }]}>
                  <InputNumber style={{ width: '100%' }} placeholder="जैसे: 2010"
                    min={1900} max={new Date().getFullYear()} />
                </Item>
              </Col>
              <Col xs={24} sm={12}>
                <Item label="व्यवसाय का शहर" name="current_city"
                  rules={[{ required: true, message: 'शहर दर्ज करें' }]}>
                  <Input prefix={<EnvironmentOutlined />} placeholder="जैसे: हैदराबाद, जयपुर" maxLength={100} />
                </Item>
              </Col>
              <Col xs={24} sm={12}>
                <Item label="राज्य" name="state"
                  rules={[{ required: true, message: 'राज्य दर्ज करें' }]}>
                  <Input prefix={<GlobalOutlined />} placeholder="जैसे: राजस्थान, तेलंगाना" maxLength={100} />
                </Item>
              </Col>
              <Col xs={24}>
                <Item label="व्यावसायिक पता" name="business_address"
                  rules={[{ validator: validateSafe('पता') }]}>
                  <TextArea rows={2} placeholder="दुकान/ऑफिस का पूरा पता" maxLength={500} />
                </Item>
              </Col>
              <Col xs={24}>
                <Item label="व्यवसाय के बारे में" name="business_about"
                  rules={[{ validator: validateSafe('व्यवसाय विवरण') }]}>
                  <TextArea rows={3} placeholder="व्यवसाय, सेवाओं या उत्पादों के बारे में लिखें..." maxLength={600} />
                </Item>
              </Col>
              <Col xs={24} sm={8}>
                <Item label="अनुभव (वर्ष) — Experience" name="experience_years">
                  <InputNumber min={0} max={70} placeholder="जैसे: 10" style={{ width: '100%' }} />
                </Item>
              </Col>
              <Col xs={24} sm={8}>
                <Item label="रैंक / पदवी — Rank" name="biz_rank"
                  rules={[{ validator: validateSafe('Rank') }]}>
                  <Input placeholder="जैसे: Gold Partner" maxLength={100} />
                </Item>
              </Col>
              <Col xs={24} sm={8}>
                <Item label="पद / Post" name="post"
                  rules={[{ validator: validateSafe('Post') }]}>
                  <Input placeholder="जैसे: Director, Proprietor" maxLength={100} />
                </Item>
              </Col>
              <Col xs={24}>
                <Item label="दुकान / व्यवसाय की फोटो (optional)" name="business_photo">
                  <MultiPhotoUpload />
                </Item>
              </Col>
            </Row>

            {/* Location */}
            <div style={sectionStyle}>मूल स्थान (गाँव / जिला)</div>
            <Row gutter={[16, 0]}>
              <Col xs={24} sm={8}>
                <Item label="गाँव" name="village"
                  rules={[{ required: true, message: 'गाँव दर्ज करें' }, { validator: validateName('गाँव') }]}>
                  <Input prefix={<EnvironmentOutlined />} placeholder="गाँव" maxLength={100} />
                </Item>
              </Col>
              <Col xs={24} sm={8}>
                <Item label="तहसील" name="tehsil"
                  rules={[{ required: true, message: 'तहसील दर्ज करें' }, { validator: validateName('तहसील') }]}>
                  <Input prefix={<EnvironmentOutlined />} placeholder="तहसील" maxLength={100} />
                </Item>
              </Col>
              <Col xs={24} sm={8}>
                <Item label="जिला" name="district"
                  rules={[{ required: true, message: 'जिला दर्ज करें' }, { validator: validateName('जिला') }]}>
                  <Input prefix={<EnvironmentOutlined />} placeholder="जिला" maxLength={100} />
                </Item>
              </Col>
            </Row>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 12 }}>
              <Button size="large" onClick={resetAll}>रद्द करें</Button>
              <Button size="large" type="primary" loading={submitting} onClick={onSubmit}
                style={{ background: '#7a0000', borderColor: '#7a0000', fontWeight: 700,
                  paddingLeft: 32, paddingRight: 32 }}>
                अपडेट अनुरोध भेजें
              </Button>
            </div>
          </Form>
        </Card>
      )}

      {/* Step 4 — success */}
      {step === 4 && (
        <Card style={{ ...cardStyle, maxWidth: 480 }}>
          <Result
            icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
            title="अपडेट अनुरोध सफलतापूर्वक भेजा गया!"
            subTitle={
              <span style={{ fontSize: 14, color: '#555' }}>
                आपके बदलाव admin की समीक्षा के लिए भेज दिए गए हैं।<br />
                मंजूरी मिलने के बाद आपकी जानकारी directory में अपडेट हो जाएगी।<br />
                तब तक आपकी पुरानी जानकारी दिखती रहेगी।
              </span>
            }
            extra={[
              <Button key="again" onClick={resetAll}>
                दोबारा अपडेट करें
              </Button>,
            ]}
          />
        </Card>
      )}
    </div>
  );
}

export default BusinessDirectoryUpdate;
